// Which sheet frame an otter shows (deck/entities.js OtterView): walking upright, jogging (the run), swimming, floating on its
// back, diving in, climbing out. Nothing new goes over the wire: "boat" (in the water) is still the office's state,
// and the rest comes from what each client sees change.
//  - on land: standing / walking (the Pokemon step cycle); the run (an upright jog: faster steps, a longer stride) while travelling (a click-to-go trip:
//    mine from the mover, `p.running`; someone else's is guessed: moving without a stop for RUN_AFTER_MS)
//  - in the water: swimming while moving, floating on its back when still
//  - getting in: the dive (a push off the edge, the leap, the splash) and getting out: half in the water, then up
//    over the edge. Both while the drawn position glides across (mine: hopGlide, the hop deck/move.js made; anyone
//    else's: the usual easing toward where they are), with a little arc, the shadow fading out / in, and facing the
//    way it was going, so an otter swimming into the shore keeps going and walks off up the deck.
//    3D: the climb frames face the way it climbs (16 directions): paws up on the edge in the water, then heaving up
//    over it on the arc, then the walk or run; stopped on land right after: the shake, facing whichever way.
//    Classic: the climb frames only face down (the haul-out and the shake there; otherwise the swim, then the walk).
//  - an emote (/emotes.js), standing still on land: its frames instead of standing (in the water: only its icon); the
//    act ones (`act`: 敲电脑 ... 比心, every body's) in the way the otter faces;
//    the 睡觉 action: the sleep below, right away and for as long as it lasts
//  - a two-person action (/duo.js duoFrame): both standing (or floating) side by side facing each other, each its half
import { ANIM, facingFrame, runFrames, OTTER3D, frames16, GEO3D, actReady, actFrames16 } from '/looks.js';
import { dir8, dir16, HOP_MS } from './move.js';
import { emoteNow, emoteStep } from '/emotes.js';
import { duoFrame } from '/duo.js';

export const RUN_AFTER_MS = 900;
const RUN_FRAME_MS = 75; // a jog: 4 frames in 300 ms
/** The walk's steps: quicker since walking got faster (5 → 6.5 tiles a second, collide.js WALK_SPEED; was 110 ms,
 *  the run 85), so the feet keep up with the ground without turning the walk into the jog. */
const WALK_FRAME_MS = 95;
const DIVE_MS = [50, 150, 170], CLIMB_WET_MS = 90, HEAVE_MS = 70, SHAKE_MS = [330, 260], HOP_ARC = 0.18, IDLE_MS = 420, SLEEP_MS = 45000, SLEEP_FRAME_MS = 700;
const GEO_SIT = OTTER3D && !!GEO3D.anim.sit; // the 3D sheets have sitting and typing (classic ones don't: they stand on the seat)
const TYPE_MS = 2600, PAUSE_MS = 1400; // at a desk: typing a while, then a pause
const GEO_SLEEP = OTTER3D && !!ANIM.sleep; // the 3D sheets have a sleep (classic ones don't: they stay standing) // the 3D idle: 6 frames, a slow breath
const DIR_SWIM = ['swimD', 'swimR', 'swimU', 'swimL'];
const sum = (a) => a.reduce((x, y) => x + y, 0);
const downish = (d16) => d16 <= 2 || d16 >= 14; // facing the camera, near enough for the classic climb frames
/** Where my otter is drawn while its hop in or out of the water (p.hop: {x, y, at}, deck/move.js boatSwitch) eases
 *  across: the offset from its position, [dx, dy] tiles, fast at first (it was moving) and settling. */
export function hopGlide(p, now) {
  const h = p.hop; if (!h) return [0, 0];
  const u = (now - h.at) / HOP_MS;
  if (!(u >= 0 && u < 1)) { p.hop = null; return [0, 0]; }
  const k = (1 - u) * (1 - u);
  return [(h.x - p.x) * k, (h.y - p.y) * k];
}
/** Which step of a timed sequence t ms in (null once it's over). */
function stepAt(durs, t) { for (let i = 0, s = 0; i < durs.length; i++) { s += durs[i]; if (t < s) return i; } return null; }

/**
 * @param {object} p an otter: {drawX, drawY, dir, dir8?, moving, boat, running?}; a little state of its own is kept on it (p._pose)
 * @param {number} now ms
 * @param {object} [look] the look it's drawn in (a cat's: its own actions)
 * @returns {{frame: number, wet: boolean, shadow?: number, ox: number, oy: number, kind: string, emote: object|null, act?: boolean, stand?: number}} the sheet
 *   frame; wet: in the water (no shadow on the ground); shadow: how much of the shadow shows (0-1) getting in or out,
 *   else 0 wet and 1 dry; ox/oy: tiles to shift the sprite by (the hop's arc);
 *   emote: the emote playing ({id, def, t}: /emotes.js emoteNow), whether or not its frames show
 */
export function otterFrame(p, now, look = p.look) {
  // a two-person action (/duo.js: 蹭蹭, 击掌...): its half, beside the partner (gx/gy), from the look's duo sheet
  if (p.duo) { const d = duoFrame(p, now, look); if (d) { p._awakeAt = now; pose(p, now); return d; } }
  const emote = emoteNow(p, now); if (emote && !emote.def.sleep) p._awakeAt = now; // an emote wakes it (睡觉 doesn't)
  const fr = pose(p, now, !!emote?.def.sleep);
  if (emote && fr.kind === 'stand' && emote.def.seq) {
    const k = emoteStep(emote.def, emote.t);
    // an act action (敲电脑 ...): its frames in the way it faces (16 ways); the classic otters: only the icon
    if (!emote.def.act) { fr.frame = ANIM[emote.def.anim][k]; fr.kind = 'emote'; }
    // (from the body's act sheet, looks.js: loaded the first time; until it's in, it stands): fr.act, fr.stand the
    // main sheet's frame to fall back on
    else if (OTTER3D && actFrames16(emote.def.anim, 0) && actReady(look)) { fr.stand = fr.frame; fr.frame = actFrames16(emote.def.anim, dir16(p))[k]; fr.act = true; fr.kind = 'emote'; }
  }
  fr.emote = emote;
  return fr;
}

function pose(p, now, asleep = false) {
  let s = p._pose;
  if (!s) s = p._pose = { boat: !!p.boat, change: -1e9, fromX: p.drawX, movingSince: now, moving: false };
  if (!!p.boat !== s.boat) { s.boat = !!p.boat; s.change = now; s.fromX = s.lastX ?? p.drawX; } // just got in or out

  if (p.moving && !s.moving) s.movingSince = now;
  if (p.moving || p.boat) s.stillSince = now;
  s.moving = !!p.moving; s.lastX = p.drawX;
  const t = now - s.change, dir = p.dir & 3, d16 = dir16(p);
  const u = Math.min(1, t / HOP_MS), arc = u < 1 ? -HOP_ARC * Math.sin(Math.PI * u) : 0; // the hop over the edge
  const swim = () => { const a = OTTER3D ? frames16('swim', d16) : ANIM[DIR_SWIM[dir]]; return a[Math.floor(now / 170) % a.length]; };
  if (p.boat) {
    const i = stepAt(DIVE_MS, t);
    if (i !== null) {
      const left = dir === 3 || (dir !== 1 && s.fromX > p.drawX);
      const frame = OTTER3D ? frames16('dive', d16)[i] : ANIM[left ? 'diveL' : 'diveR'][i], shadow = Math.max(0, 1 - u * 1.6);
      return { frame, wet: !shadow, shadow, ox: 0, oy: arc, kind: 'dive' };
    }
    if (p.moving) return { frame: swim(), wet: true, ox: 0, oy: 0, kind: 'swim' };
    return { frame: ANIM.float[Math.floor(now / 650) % 2], wet: true, ox: 0, oy: 0, kind: 'float' };
  }
  // 3D climb frames (16 directions): 0 paws up on the edge in the water, 1 heaving up over it, 2 the shake.
  // Classic (facing down only): 0 the haul-out, 1 the shake, 2 standing.
  const climb = OTTER3D ? frames16('climb', d16) : downish(d16) ? ANIM.climb : null;
  if (t < CLIMB_WET_MS) return { frame: climb ? climb[0] : swim(), wet: true, shadow: 0, ox: 0, oy: arc, kind: 'climb' };
  const fr = onLand(p, now, s, asleep);
  if (t < HOP_MS) {
    Object.assign(fr, { oy: arc, shadow: Math.min(1, (t - CLIMB_WET_MS) / (HOP_MS - CLIMB_WET_MS)), kind: 'climb' });
    if (OTTER3D && t < CLIMB_WET_MS + HEAVE_MS) fr.frame = climb[1]; // up over the edge, then straight into the walk / run
  } else if (!p.moving && climb && t < HOP_MS + sum(SHAKE_MS)) { // stopped right after: the shake (3D: then standing breathing)
    const i = stepAt(SHAKE_MS, t - HOP_MS);
    if (!OTTER3D) Object.assign(fr, { frame: climb[1 + i], kind: 'climb' });
    else if (i === 0) Object.assign(fr, { frame: climb[2], kind: 'climb' });
  }
  return fr;
}

/** On the deck: walking, the jog, standing, asleep. */
function onLand(p, now, s, asleep = false) {
  const running = p.moving && (p.running ?? now - s.movingSince >= RUN_AFTER_MS);
  // on land, 8 ways (dir8: the 3/4 views on the diagonals) or with the 3D otters 16 (dir16); in the water, getting in
  // and out, 4 (dir) or 16
  const d8 = dir8(p);
  if (OTTER3D) { // 16 ways: the jog, a 4-frame walk, and standing still breathing and looking about (each otter on its own beat)
    const d = dir16(p), seq = (a, ms, off = 0) => { const f = frames16(a, d); return f[Math.floor((now + off) / ms) % f.length]; };
    if (running) return { frame: seq('run', RUN_FRAME_MS), wet: false, ox: 0, oy: 0, kind: 'run' };
    if (p.moving) return { frame: seq('walk', WALK_FRAME_MS), wet: false, ox: 0, oy: 0, kind: 'walk' };
    s.beat ??= Math.floor(Math.random() * 4000);
    // on a seat (p._seat: deck/scene.js from public/sit.js): sitting, facing the seat's way, up onto it by its lift;
    // at a desk typing, with pauses. Never asleep there.
    const seat = p._seat;
    if (seat && GEO_SIT) {
      const typing = seat.kind === 'desk' && (now + s.beat) % (TYPE_MS + PAUSE_MS) < TYPE_MS, a = typing ? 'type' : 'sit', f = frames16(a, seat.dir16);
      return { frame: f[Math.floor((now + s.beat) / GEO3D.anim[a].ms) % f.length], wet: false, ox: 0, oy: -(seat.lift || 0) / 16, kind: 'sit', ...(seat.lift > 0 ? { shadow: 0 } : {}),
        tagY: seat.kind === 'desk' ? -0.35 : -Math.max(0, seat.lift || 0) / 16 }; // the name tag: over the laptop at a desk, up with a raised seat
    }
    // asleep after SLEEP_MS standing still and quiet (no move, turn, emote, chat or speech: p._awakeAt), on each
    // client from what it sees; nothing new on the wire. Down-facing (it lies 3/4), a slow breath and a z z
    if (s.d16 !== d) { s.d16 = d; s.stillSince = now; }
    if (GEO_SLEEP && (asleep || now - Math.max(s.stillSince ?? now, p._awakeAt ?? 0) > SLEEP_MS)) return { frame: seq('sleep', SLEEP_FRAME_MS, s.beat), wet: false, ox: 0, oy: 0, kind: 'sleep' };
    return { frame: seq('idle', IDLE_MS, s.beat), wet: false, ox: 0, oy: 0, kind: 'stand' };
  }
  if (running) { const a = runFrames(d8); return { frame: a[Math.floor(now / RUN_FRAME_MS) % a.length], wet: false, ox: 0, oy: 0, kind: 'run' }; }
  // Pokemon-style walk: step A, stand, step B, stand (the body's 1-px rise on a step is drawn in the frames)
  const cycle = [1, 0, 2, 0], f = p.moving ? cycle[Math.floor(now / WALK_FRAME_MS) % 4] : 0;
  return { frame: facingFrame(d8, f), wet: false, ox: 0, oy: 0, kind: p.moving ? 'walk' : 'stand' };
}
